import { colors } from '@/theme'
import { formatDateTime } from '@/lib/utils'
import { CLINICAL_REVIEW_STATUS, getAutoAdvanceDecision } from '@/lib/med-request-on-hold'
import { medRequestStatusValueToLabel } from '@/constants/statuses'
import type { MedicationRequest, OnHoldEntry } from '@/services/med-req-detail'
import { useUpdateMedReqDetailsMutation } from '@/services/med-req-detail'
import { Edit } from '@mui/icons-material'
import { Box, Button, CircularProgress, Divider, Grid, IconButton, TextField, Typography } from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import CollapsibleSection from './collapsible-section'
import SendFanEmailDialog from './send-fan-email-dialog'

interface OnHoldHistoryProps {
  medRequest: MedicationRequest
  readOnly?: boolean
}

interface EntryEditState {
  reason: string
  patientResponse: string
}

function entryToEditState(entry: OnHoldEntry): EntryEditState {
  return {
    reason: entry.reason ?? '',
    patientResponse: entry.patientResponse ?? ''
  }
}

function OnHoldEntryRow({
  entry,
  onSave,
  readOnly,
  isSaving
}: {
  entry: OnHoldEntry
  onSave: (values: EntryEditState, baseline: EntryEditState | null) => Promise<void>
  readOnly?: boolean
  isSaving?: boolean
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [currentOnHold, setCurrentOnHold] = useState<EntryEditState | null>(null)
  const [values, setValues] = useState<EntryEditState>(entryToEditState(entry))

  const baseline = currentOnHold ?? entryToEditState(entry)
  const hasChanges =
    baseline.reason.trim() !== values.reason.trim() || baseline.patientResponse.trim() !== values.patientResponse.trim()

  const handleEdit = () => {
    const snapshot = entryToEditState(entry)
    setCurrentOnHold(snapshot)
    setValues(snapshot)
    setIsEditing(true)
  }

  const handleSave = async () => {
    await onSave(values, currentOnHold)
    setCurrentOnHold(null)
    setIsEditing(false)
  }

  const handleCancel = () => {
    if (isSaving) return
    setCurrentOnHold(null)
    setValues(entryToEditState(entry))
    setIsEditing(false)
  }

  return (
    <Box>
      {entry.date && (
        <Typography sx={{ color: colors.text.dark, fontSize: '12px', fontWeight: 500, mb: 1 }}>
          {formatDateTime(entry.date)}
        </Typography>
      )}

      <Grid
        container
        sx={{
          '& > *:not(:first-child)': { pl: 3 }
        }}
      >
        {/* On Hold Reason */}
        <Grid size={6}>
          <Typography sx={{ color: colors.text.secondary, fontSize: '12px', mb: 0.5 }}>On Hold Reason</Typography>
          {isEditing ? (
            <TextField
              multiline
              rows={3}
              fullWidth
              disabled={isSaving}
              value={values.reason}
              onChange={e => setValues(v => ({ ...v, reason: e.target.value }))}
              placeholder='Enter reason'
              sx={textFieldSx}
            />
          ) : (
            <Typography sx={{ color: colors.text.dark, fontSize: '14px', lineHeight: 1.6 }}>
              {values.reason || '-'}
            </Typography>
          )}
        </Grid>

        {/* Patient Response */}
        <Grid size={6}>
          <Typography sx={{ color: colors.text.secondary, fontSize: '12px', mb: 0.5 }}>Patient Response</Typography>
          {isEditing ? (
            <TextField
              multiline
              rows={3}
              fullWidth
              disabled={isSaving}
              value={values.patientResponse}
              onChange={e => setValues(v => ({ ...v, patientResponse: e.target.value }))}
              placeholder='Enter patient response'
              sx={textFieldSx}
            />
          ) : (
            <Typography sx={{ color: colors.text.dark, fontSize: '14px', lineHeight: 1.6 }}>
              {values.patientResponse || '-'}
            </Typography>
          )}
        </Grid>

        {!isEditing && !readOnly && (
          <IconButton size='small' sx={{ ml: 'auto' }} onClick={handleEdit}>
            <Edit sx={{ fontSize: '16px', color: colors.brand.primary }} />
          </IconButton>
        )}
      </Grid>

      {isEditing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          {isSaving && <CircularProgress size={20} sx={{ color: colors.brand.primary }} />}
          <Button variant='ghost' onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSave} disabled={isSaving || !hasChanges}>
            {isSaving ? 'Saving...' : 'Save information'}
          </Button>
        </Box>
      )}
    </Box>
  )
}

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '14px',
    '& fieldset': { borderColor: colors.border.default },
    '&:hover fieldset': { borderColor: colors.border.hover },
    '&.Mui-focused fieldset': { borderColor: colors.brand.primary }
  }
}

export default function OnHoldHistory({ medRequest, readOnly }: OnHoldHistoryProps) {
  const onHoldHistory = medRequest.onHoldHistory || []
  const [showFanEmailDialog, setShowFanEmailDialog] = useState(false)
  const queryClient = useQueryClient()
  const updateDetailsMutation = useUpdateMedReqDetailsMutation()

  const handleEntrySave = async (index: number, values: EntryEditState, baseline: EntryEditState | null) => {
    const fallbackBaseline = entryToEditState(onHoldHistory[index] ?? { reason: '', patientResponse: '', date: '' })
    const effectiveBaseline = baseline ?? fallbackBaseline

    const advanceDecision = getAutoAdvanceDecision({
      baseline: effectiveBaseline,
      next: values,
      currentStageValue: medRequest.stage
    })

    if (!advanceDecision.hasManualChange) {
      return
    }

    const shouldAdvance = advanceDecision.shouldAdvance

    let updatedHistory: OnHoldEntry[] = []
    if (index === onHoldHistory.length) {
      updatedHistory.push({
        reason: values.reason,
        patientResponse: values.patientResponse,
        date: new Date().toISOString()
      })
    } else {
      updatedHistory = onHoldHistory.map((entry, i) =>
        i === index
          ? {
              ...entry,
              reason: values.reason,
              patientResponse: values.patientResponse
            }
          : entry
      )
    }

    let data: { on_hold_history: string; stage?: string } = {
      on_hold_history: JSON.stringify(updatedHistory)
    }

    if (shouldAdvance) {
      data = { ...data, stage: CLINICAL_REVIEW_STATUS }
    }

    await updateDetailsMutation.mutateAsync({
      orderItemHash: medRequest.id,
      data
    })

    queryClient.invalidateQueries({ queryKey: ['med-request-detail', medRequest.id] })
    queryClient.invalidateQueries({ queryKey: ['timeline', 'medication_request', medRequest.id] })

    if (shouldAdvance) {
      enqueueSnackbar('On hold information saved. Status updated to Clinical Review.', { variant: 'success' })
    } else if (!advanceDecision.inWhitelist) {
      const stageLabel = medRequestStatusValueToLabel(medRequest.stage)
      enqueueSnackbar(
        `On hold information saved. Status was not changed (current stage "${stageLabel}" is not eligible for auto-advance to Clinical Review).`,
        { variant: 'info' }
      )
    } else {
      enqueueSnackbar('On hold information saved successfully.', { variant: 'success' })
    }
  }

  return (
    <CollapsibleSection
      id='on-hold'
      title='On Hold History'
      actionButton={
        readOnly ? undefined : { label: 'Send & schedule FAN emails', onClick: () => setShowFanEmailDialog(true) }
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* On Hold entries */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            p: 3,
            bgcolor: colors.background.surface,
            borderRadius: '12px'
          }}
        >
          {(onHoldHistory.length > 0 ? onHoldHistory : [{ reason: '', patientResponse: '', date: '' }]).map(
            (entry, index, arr) => (
              <Box key={index}>
                <OnHoldEntryRow
                  entry={entry}
                  onSave={(values, baseline) => handleEntrySave(index, values, baseline)}
                  readOnly={readOnly}
                  isSaving={updateDetailsMutation.isPending}
                />
                {index < arr.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            )
          )}
        </Box>
      </Box>

      <SendFanEmailDialog
        open={showFanEmailDialog}
        medRequest={medRequest}
        onClose={() => setShowFanEmailDialog(false)}
        categoryFilter='fan'
      />
    </CollapsibleSection>
  )
}
